import React from 'react';
import moment from 'moment';
import { useTable, usePagination, useSortBy, Column } from 'react-table';
import { useHistory } from 'react-router-dom';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { User, UserRole } from '@t/user';
import { DATE_TIME_FORMAT } from '@config';
import LoaderVertical from '@components/LoaderVertical';

import styles from './styles.module.css';

export interface Props {
    managers: User[];
    loading: boolean;
}

const columns: Column<User>[] = [
    {
        Header: 'Имя',
        accessor: 'firstName',
    },
    {
        Header: 'Фитнес-площадка',
        accessor: (manager: User) => {
            return {
                title: manager.gymsTitle.map(
                    (gymTitle) => gymTitle && `${gymTitle}; `
                ),
                styleClass: styles.line,
            };
        },
        Cell: ({ cell: { value } }: any) => (
            <div className={value.styleClass}>{value.title}</div>
        ),
    },
    {
        Header: 'Email',
        accessor: 'email',
    },
    {
        Header: 'Телефон',
        accessor: 'phone',
    },
    {
        Header: 'Дата регистрации',
        accessor: (manager: User) =>
            moment(manager.createdAt).format(DATE_TIME_FORMAT),
    },
    {
        Header: 'Роль',
        accessor: (manager: User) =>
            manager.role === UserRole.Manager
                ? 'Менеджер'
                : manager.role === UserRole.Admin
                ? 'Администратор'
                : manager.role === UserRole.SuperAdmin
                ? 'Супер администратор'
                : manager.role === UserRole.Finance
                ? 'Финансы'
                : manager.role === UserRole.Analytics
                ? 'Аналитик'
                : 'HR',
    },
    {
        Header: 'Статус',
        accessor: (manager: User) => {
            return {
                title: manager.verified ? 'Верифицирован' : 'Не верифицирован',
                styleClass: !manager.verified
                    ? styles.notVerified
                    : styles.verified,
            };
        },
        Cell: ({ cell: { value } }: any) => (
            <div className={value.styleClass}>{value.title}</div>
        ),
    },
];

export const ManagerListTable: React.FC<Props> = ({ managers, loading }) => {
    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
    } = useTable(
        {
            columns,
            data: managers || [],
            initialState: {
                pageIndex: 0,
                pageSize: 10000,
            },
        },
        useSortBy,
        usePagination
    );
    const history = useHistory();

    return (
        <>
            <table {...getTableProps()} className={styles.table}>
                <thead>
                    {headerGroups.map((headerGroup) => (
                        <tr
                            {...headerGroup.getHeaderGroupProps()}
                            key={Math.random()}
                        >
                            {headerGroup.headers.map((column) => (
                                <th
                                    {...column.getHeaderProps(
                                        column.getSortByToggleProps()
                                    )}
                                    key={Math.random()}
                                >
                                    {column.render('Header')}
                                    <span className={styles.sorted}>
                                        <FontAwesomeIcon
                                            icon={
                                                column.isSorted
                                                    ? column.isSortedDesc
                                                        ? 'sort-up'
                                                        : 'sort-down'
                                                    : 'sort'
                                            }
                                        />
                                    </span>
                                </th>
                            ))}
                        </tr>
                    ))}
                </thead>
                <tbody {...getTableBodyProps()}>
                    {page.map((row) => {
                        prepareRow(row);
                        const onClick = () => {
                            const manager = row.original;
                            history.push(`/dashboard/managers/${manager._id}`);
                        };
                        return (
                            <tr
                                {...row.getRowProps()}
                                className={styles.tableRow}
                                onClick={onClick}
                                key={Math.random()}
                            >
                                {row.cells.map((cell) => {
                                    return (
                                        <td
                                            {...cell.getCellProps()}
                                            key={Math.random()}
                                        >
                                            {cell.render('Cell')}
                                        </td>
                                    );
                                })}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
            {loading && <LoaderVertical />}
        </>
    );
};

export default ManagerListTable;
